import { FaUser, FaKey } from "react-icons/fa";
import {
  Button,
  Form,
  FormItem,
  Input,
  TextArea,
  useForm,
  Message,
  Select,
} from "@/frontend/components/common";
import { roleService } from "@/frontend/services";
import { useNavigate } from "react-router-dom";
import type { CreateRoleRequestDTO, ApiResponse, RoleResponseDTO } from "@/shared/dto";
import { useEffect } from "react";
import { PlatformAccessEnum, StatusGenericEnum } from "@/core/enums";

interface CreateRoleFormValues {
  name: string;
  guardSlug: string;
  description?: string;
  status: "active" | "inactive" | "deleted";
  platformAccess: "web" | "mobile" | "both";
}
interface RoleFormProps {
  roleId?: string;
  initialValues?: Partial<CreateRoleFormValues>;
}
export function RoleForm({ roleId, initialValues }: RoleFormProps) {
  const navigate = useNavigate();
  const [form] = useForm();
  const isEditMode = !!roleId;

  useEffect(() => {
    if (initialValues && isEditMode) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, isEditMode, form]);

  const handleSubmit = async (values: CreateRoleFormValues) => {
    try {
      const roleData: CreateRoleRequestDTO = {
        name: values.name,
        guardSlug: values.guardSlug,
        description: values.description || undefined,
        status: values.status,
        platformAccess: values.platformAccess,
      };

      let response: ApiResponse<RoleResponseDTO>;

      if (isEditMode && roleId) {
        response = await roleService.update(roleId, roleData);
      } else {
        response = await roleService.create(roleData);
      }

      if (response.success && response.data) {
        if (!isEditMode) {
          Message.success({ content: "Rol creado exitosamente" });
          const { id } = response.data;
          navigate(`/roles/edit/${id}`);
        } else {
          Message.success({ content: "Rol actualizado exitosamente" });
        }
        navigate("/roles");
      } else {
        Message.error({
          content: response.error || (isEditMode ? "Error al actualizar rol" : "Error al crear rol"),
        });
      }
    } catch (err) {
      console.log(err);
      const errorMessage =
        err instanceof Error ? err.message : "Error al crear rol";

      if (errorMessage.toLowerCase().includes("name")) {
        form.setFields([
          {
            name: "name",
            errors: [errorMessage],
          },
        ]);
      } else if (errorMessage.toLowerCase().includes("guard")) {
        form.setFields([
          {
            name: "guardSlug",
            errors: [errorMessage],
          },
        ]);
      } else {
        Message.error({ content: errorMessage });
      }
    }
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      className="w-full"
      initialValues={{
        status: "active",
        platformAccess: "both",
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Name */}
        <FormItem
          name="name"
          label="Nombre del Rol"
          rules={[{ required: true, message: "El nombre es requerido" }]}
        >
          <Input
            prefix={<FaUser className="text-gray-400" />}
            placeholder="Ej: Administrador"
            size="large"
          />
        </FormItem>

        {/* Guard Slug */}
        <FormItem
          name="guardSlug"
          label="Guard Slug"
          rules={[
            { required: true, message: "El guard slug es requerido" },
            {
              pattern: /^[a-z0-9_]+$/,
              message: "Solo letras minúsculas, números y guiones bajos",
            },
          ]}
        >
          <Input
            prefix={<FaKey className="text-gray-400" />}
            placeholder="Ej: admin, user_manager"
            size="large"
          />
        </FormItem>

        {/* Description */}
        <FormItem
          name="description"
          label="Descripción"
          className="md:col-span-2"
        >
          <TextArea
            placeholder="Descripción del rol (opcional)"
            rows={3}
            size="large"
          />
        </FormItem>

        {/* Status */}
        <FormItem
          name="status"
          label="Estado"
          rules={[{ required: true, message: "El estado es requerido" }]}
        >
          <Select
            size="large"
            placeholder="Selecciona un estado"
            options={[
              { value: StatusGenericEnum.ACTIVE, label: "Activo" },
              { value: StatusGenericEnum.INACTIVE, label: "Inactivo" },
              { value: StatusGenericEnum.DELETED, label: "Eliminado" },
            ]}
          />
        </FormItem>

        {/* Platform Access */}
        <FormItem
          name="platformAccess"
          label="Acceso a Plataforma"
          rules={[{ required: true, message: "El acceso a plataforma es requerido" }]}
        >
          <Select options={[
            { value: PlatformAccessEnum.WEB, label: "Web" },
            { value: PlatformAccessEnum.MOBILE, label: "Móvil" },
            { value: PlatformAccessEnum.BOTH, label: "Ambos" }]}
            placeholder="Selecciona el acceso"
            size="large"
          />
        </FormItem>
      </div>

      <div className="flex justify-end gap-4 mt-6">
        <Button
          type="primary"
          htmlType="submit"
          size="large"
        >
          {isEditMode ? "Actualizar Rol" : "Crear Rol"}
        </Button>
        <Button
          size="large"
          onClick={() => navigate("/roles")}
        >
          Cancelar
        </Button>
      </div>
    </Form>
  );
}
